import type { UserSyncSource } from '@/api/types';
import { useGuardarConfiguracion } from './useGuardarConfiguracion';
import { ConfigCard, AvisoDeGuardado } from './ConfigCard';

const USER_SOURCES: { value: UserSyncSource; labelKey: string; hintKey: string }[] = [
  { value: 'AdOnly',    labelKey: 'jobs.sync.adOnly',    hintKey: 'jobs.sync.adOnlyHint' },
  { value: 'ApiOnly',   labelKey: 'jobs.sync.apiOnly',   hintKey: 'jobs.sync.apiOnlyHint' },
  { value: 'AdThenApi', labelKey: 'jobs.sync.adThenApi', hintKey: 'jobs.sync.adThenApiHint' },
];

/**
 * De dónde sale el padrón de colaboradores, y el estado de SuccessFactors.
 *
 * Los tres caminos son los que acordó la junta (N8 / D-U4) y se construyeron los tres: aquí sólo se
 * elige cuál está activo. Los defaults del backend son conservadores (AdOnly + SuccessFactors
 * apagado y en simulación), así que la pantalla arranca sin que nada haya cambiado.
 *
 * Los dos interruptores de SuccessFactors viven en ESTA pestaña y no en la de unidades
 * organizacionales, aunque gobiernen las dos corridas. Duplicar el control sería dos mandos para un
 * mismo valor; en la otra pestaña se muestra su estado en modo lectura, diciendo dónde se cambia.
 */
export function UserSourceCard() {
  const { t, data, isLoading, isError, guardando, guardar, aviso, mensajeDeError } = useGuardarConfiguracion();

  if (isLoading) return <p className="jobs-card-hint" role="status">{t('common.loading')}</p>;
  if (isError || !data) return <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.loadError')}</p>;

  return (
    <ConfigCard
      icono="fa-database"
      titulo={t('jobs.sync.title')}
      descripcion={t('jobs.sync.desc')}
      pie={
        <>
          <AvisoDeGuardado aviso={aviso} error={mensajeDeError} />
          {data.updatedBy && (
            <p className="jobs-card-meta">{t('jobs.sync.updatedBy')} {data.updatedBy}</p>
          )}
        </>
      }
    >
      <fieldset className="jobs-fieldset" disabled={guardando}>
        <legend>{t('jobs.sync.users')}</legend>
        {USER_SOURCES.map(source => (
          <label key={source.value} className="jobs-choice">
            <input
              type="radio"
              name="userSource"
              value={source.value}
              checked={data.userSource === source.value}
              onChange={() => guardar({ userSource: source.value }, t('jobs.sync.saved'))}
            />
            <span>
              <strong>{t(source.labelKey)}</strong>
              <small>{t(source.hintKey)}</small>
            </span>
          </label>
        ))}
      </fieldset>

      <fieldset className="jobs-fieldset" disabled={guardando}>
        <legend>{t('jobs.sync.sf')}</legend>
        <label className="jobs-choice">
          <input
            type="checkbox"
            checked={data.successFactors.enabled}
            onChange={e => guardar({ enabled: e.target.checked }, t('jobs.sync.saved'))}
          />
          <span><strong>{t('jobs.sync.sfEnabled')}</strong></span>
        </label>
        <label className="jobs-choice">
          <input
            type="checkbox"
            checked={data.successFactors.dryRun}
            onChange={e => guardar({ dryRun: e.target.checked }, t('jobs.sync.saved'))}
          />
          <span>
            <strong>{t('jobs.sync.dryRun')}</strong>
            <small>{t('jobs.sync.dryRunHint')}</small>
          </span>
        </label>
      </fieldset>
    </ConfigCard>
  );
}
